import React, { useState, useEffect } from "react";
import { Save, Moon, Coffee, Droplets, Clock, RotateCcw } from "lucide-react";
import DietaryPreferences from "./DietaryPreferences";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
      } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">×</button>
    </div>
  );
};

const Lifestyle = ({ setActiveComponent }) => {
  const [toasts, setToasts] = useState([]);
  const [activeScreen, setActiveScreen] = useState("lifestyle");
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [lifestyleData, setLifestyleData] = useState({
    wakeupTime: "",
    sleepingTime: "",
    sleepHours: "",
    alcoholConsumption: false,
    alcoholFrequency: "",
    alcoholType: "",
    alcoholQuantity: "",
    smoking: false,
    cigarettesPerDay: "",
    drugConsumption: false,
    drugFrequency: "",
    dailyWaterIntake: "",
    sittingJob: false,
    travelFrequency: "",
    physicalActivityLevel: "",
    exerciseTypes: "",
    supplements: ""
  });

  const [formErrors, setFormErrors] = useState({});

  const activityOptions = [
    { label: "Sedentary", description: "Little to no exercise" },
    { label: "Light", description: "Light exercise or sports 1-3 days a week" },
    { label: "Moderate", description: "Moderate exercise 3-5 days a week" },
    { label: "Intense", description: "Intense exercise 6-7 days a week" }
  ];

  const travelOptions = ["Never", "Occasionally", "Frequently", "Very Frequently"];
  const alcoholFrequencyOptions = ["Occasionally", "Weekly", "Daily"];
  const alcoholTypeOptions = ["Wine", "Beer", "Whisky", "Vodka", "Rum", "Other"];
  const drugFrequencyOptions = ["Occasionally", "Weekly", "Daily"];

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    fetchLifestyleData();
  }, []);

  // ✅ FIXED: Correct sleep hours calculation
  const calculateSleepHours = (wakeupTime, sleepingTime) => {
    if (!wakeupTime || !sleepingTime) return "";

    const parseTime = (timeStr) => {
      if (timeStr.includes(':')) {
        const [hours, minutes] = timeStr.split(':');
        return new Date(2000, 0, 1, parseInt(hours), parseInt(minutes) || 0);
      }
      return null;
    };

    let wakeup = parseTime(wakeupTime);
    let sleeping = parseTime(sleepingTime);

    if (!wakeup || !sleeping) return "";

    // Calculate difference in hours
    let diffHours = (wakeup - sleeping) / (1000 * 60 * 60);
    
    // If sleeping time is after wakeup time (e.g., sleep 23:00, wake 06:00)
    // diffHours will be negative, so add 24 hours
    if (diffHours < 0) {
      diffHours = 24 + diffHours;
    }
    
    // Safety check: if diffHours is more than 12, it might be inverted
    // Usually sleep should be between 4-12 hours
    if (diffHours > 12) {
      diffHours = Math.abs(diffHours - 24);
    }
    
    return diffHours.toFixed(1);
  };

 const fetchLifestyleData = async () => {
  try {
    setLoading(true);
    const res = await getProfileStepDetails();

    if (res?.status) {
      const lifestyleFields = res?.profile_completion?.steps?.lifestyle?.fields;

      const fieldMap = {};
      lifestyleFields.forEach((item) => {
        fieldMap[item.name] = item.value;
      });

      let calculatedSleepHours = fieldMap.sleepHours;
      if (fieldMap.wakeupTime && fieldMap.sleepingTime && !calculatedSleepHours) {
        calculatedSleepHours = calculateSleepHours(fieldMap.wakeupTime, fieldMap.sleepingTime);
      }

      setLifestyleData({
        wakeupTime: fieldMap.wakeupTime || "",
        sleepingTime: fieldMap.sleepingTime || "",
        sleepHours: calculatedSleepHours || fieldMap.sleepHours || "",
        alcoholConsumption: fieldMap.alcoholConsumption || false,
        alcoholFrequency: fieldMap.alcoholFrequency || "",
        alcoholType: fieldMap.alcoholType || "",
        alcoholQuantity: fieldMap.alcoholQuantity || "",
        smoking: fieldMap.smoking || false,
        cigarettesPerDay: fieldMap.cigarettesPerDay || "",
        drugConsumption: fieldMap.drugConsumption || false,
        drugFrequency: fieldMap.drugFrequency || "",
        // Clean the dailyWaterIntake value - if it's a number, keep it as number, else empty
        dailyWaterIntake: fieldMap.dailyWaterIntake !== null && fieldMap.dailyWaterIntake !== undefined 
          ? Number(fieldMap.dailyWaterIntake) 
          : "",
        sittingJob: fieldMap.sittingJob || false,
        travelFrequency: fieldMap.travelFrequency || "",
        physicalActivityLevel: fieldMap.physicalActivityLevel || "",
        exerciseTypes: fieldMap.exerciseTypes || "",
        supplements: fieldMap.supplements || ""
      });
    }
  } catch (error) {
    console.error("Error fetching lifestyle data:", error);
    addToast("Failed to load lifestyle data", "error");
  } finally {
    setLoading(false);
  }
};

  const handleTimeChange = (field, value) => {
    setLifestyleData(prev => {
      const newData = { ...prev, [field]: value };
      if ((field === "wakeupTime" || field === "sleepingTime") && newData.wakeupTime && newData.sleepingTime) {
        const sleepHours = calculateSleepHours(newData.wakeupTime, newData.sleepingTime);
        newData.sleepHours = sleepHours;
      }
      return newData;
    });
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

const handleInputChange = (field, value) => {
  if (field === "dailyWaterIntake") {
    const cleanValue = value === "" ? "" : parseFloat(value);
    setLifestyleData(prev => ({ ...prev, [field]: cleanValue === "" ? "" : cleanValue }));
  } else {
    setLifestyleData(prev => ({ ...prev, [field]: value }));
  }
  if (formErrors[field]) {
    setFormErrors(prev => ({ ...prev, [field]: "" }));
  }
};

  const validateForm = () => {
    const errors = {};

    if (!lifestyleData.wakeupTime) errors.wakeupTime = "Wakeup time is required";
    if (!lifestyleData.sleepingTime) errors.sleepingTime = "Sleeping time is required";

    if (lifestyleData.alcoholConsumption) {
      if (!lifestyleData.alcoholFrequency) errors.alcoholFrequency = "Please select frequency";
      if (!lifestyleData.alcoholType) errors.alcoholType = "Please select type";
      if (!lifestyleData.alcoholQuantity) errors.alcoholQuantity = "Please enter quantity";
    }

    if (lifestyleData.smoking && !lifestyleData.cigarettesPerDay) {
      errors.cigarettesPerDay = "Please enter number of cigarettes per day";
    }

    if (lifestyleData.drugConsumption && !lifestyleData.drugFrequency) {
      errors.drugFrequency = "Please select frequency";
    }

    if (!lifestyleData.dailyWaterIntake) errors.dailyWaterIntake = "Daily water intake is required";
    if (!lifestyleData.physicalActivityLevel) errors.physicalActivityLevel = "Physical activity level is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSaveChanges = async () => {
  if (validateForm()) {
    setIsSaving(true);

    try {
      const lifestylePayload = {
        wakeupTime: lifestyleData.wakeupTime || null,
        sleepingTime: lifestyleData.sleepingTime || null,
        sleepHours: lifestyleData.sleepHours ? parseFloat(lifestyleData.sleepHours) : null,
        alcoholConsumption: lifestyleData.alcoholConsumption || false,
        alcoholFrequency: lifestyleData.alcoholFrequency?.trim() || null,
        alcoholType: lifestyleData.alcoholType?.trim() || null,
        alcoholQuantity: lifestyleData.alcoholQuantity?.trim() || null,
        smoking: lifestyleData.smoking || false,
        cigarettesPerDay: lifestyleData.cigarettesPerDay ? parseInt(lifestyleData.cigarettesPerDay) : null,
        drugConsumption: lifestyleData.drugConsumption || false,
        drugFrequency: lifestyleData.drugFrequency?.trim() || null,
        dailyWaterIntake: lifestyleData.dailyWaterIntake !== "" && lifestyleData.dailyWaterIntake !== null && lifestyleData.dailyWaterIntake !== undefined 
          ? Number(lifestyleData.dailyWaterIntake) 
          : null,
        sittingJob: lifestyleData.sittingJob || false,
        travelFrequency: lifestyleData.travelFrequency?.trim() || null,
        physicalActivityLevel: lifestyleData.physicalActivityLevel?.trim() || null,
        exerciseTypes: lifestyleData.exerciseTypes?.trim() || null,
        supplements: lifestyleData.supplements?.trim() || null
      };
        const response = await updateProfileStep("lifestyle", lifestylePayload);
        if (response?.status) {
          addToast("Lifestyle information saved successfully!", "success");
          await fetchLifestyleData();

          setTimeout(() => {
            if (setActiveComponent) {
              setActiveComponent("dietaryPreferences");
            }
          }, 1500);
        } else {
          addToast(response?.message || "Failed to save data", "error");
        }
      } catch (error) {
        console.error("Save error:", error);
        addToast(error.message || "Failed to save lifestyle information", "error");
      } finally {
        setIsSaving(false);
      }
    } else {
      addToast("Please fill in all required fields", "error");
      const firstErrorField = Object.keys(formErrors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
    }
  };

  const handleCancel = () => {
    fetchLifestyleData();
    setFormErrors({});
    addToast("Changes discarded", "info");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
      </div>
    );
  }

  if (activeScreen === "dietaryPreferences") {
    return <DietaryPreferences />;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
            Lifestyle Habits
          </h2>
          <p className="text-[#98a19b] text-sm">
            Update your daily activities, sleep patterns, and lifestyle choices.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="h-[38px] px-4 border border-[#dfe5df] text-[#4d5954] rounded-md text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RotateCcw size={15} />
            Discard
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="h-[38px] px-4 bg-[#57b26a] text-white rounded-md text-sm flex items-center gap-2 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={15} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Sleep Patterns */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Moon size={16} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Sleep Patterns
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Wakeup time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="time"
                    name="wakeupTime"
                    value={lifestyleData.wakeupTime}
                    onChange={(e) => handleTimeChange("wakeupTime", e.target.value)}
                    className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.wakeupTime
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                      }`}
                    style={{ paddingRight: '40px' }}
                  />
                  <Clock
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a19b] pointer-events-none"
                  />
                </div>
                {formErrors.wakeupTime && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.wakeupTime}</p>
                )}
              </div>

              <div>
                <label className="block text-[13px] text-[#24312d] mb-2">
                  Sleeping time <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="time"
                    name="sleepingTime"
                    value={lifestyleData.sleepingTime}
                    onChange={(e) => handleTimeChange("sleepingTime", e.target.value)}
                    className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.sleepingTime
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                      }`}
                    style={{ paddingRight: '40px' }}
                  />
                  <Clock
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a19b] pointer-events-none"
                  />
                </div>
                {formErrors.sleepingTime && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.sleepingTime}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-[13px] text-[#24312d] mb-2">
                  No. of Hours Sleep per day
                </label>
                <input
                  type="text"
                  value={lifestyleData.sleepHours}
                  disabled
                  className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-[11px] text-[#98a19b] mt-1">Auto-calculated from wakeup and sleeping time</p>
              </div>
            </div>
          </div>

          {/* Substances & Habits */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Coffee size={16} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Substances & Habits
              </h3>
            </div>

            {/* Alcohol */}
            <div className="mb-5 pb-5 border-b border-[#edf1ed]">
              <div className="flex items-center justify-between mb-4">
                <label className="text-[13px] font-medium text-[#24312d]">
                  Do you consume Alcohol?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lifestyleData.alcoholConsumption}
                    onChange={(e) => handleInputChange("alcoholConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {lifestyleData.alcoholConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>

              {lifestyleData.alcoholConsumption && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-[12px] text-[#98a19b] mb-1">
                      How frequently? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="alcoholFrequency"
                      value={lifestyleData.alcoholFrequency}
                      onChange={(e) => handleInputChange("alcoholFrequency", e.target.value)}
                      className={`w-full h-[38px] border rounded-md px-3 text-sm outline-none ${formErrors.alcoholFrequency ? "border-red-500" : "border-[#e1e6e1]"
                        }`}
                    >
                      <option value="" disabled>Select</option>
                      {alcoholFrequencyOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#98a19b] mb-1">
                      Which type? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="alcoholType"
                      value={lifestyleData.alcoholType}
                      onChange={(e) => handleInputChange("alcoholType", e.target.value)}
                      className={`w-full h-[38px] border rounded-md px-3 text-sm outline-none ${formErrors.alcoholType ? "border-red-500" : "border-[#e1e6e1]"
                        }`}
                    >
                      <option value="" disabled>Select</option>
                      {alcoholTypeOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#98a19b] mb-1">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="alcoholQuantity"
                      value={lifestyleData.alcoholQuantity}
                      onChange={(e) => handleInputChange("alcoholQuantity", e.target.value)}
                      placeholder="e.g., 90 ml twice a week"
                      className={`w-full h-[38px] border rounded-md px-3 text-sm outline-none ${formErrors.alcoholQuantity ? "border-red-500" : "border-[#e1e6e1]"
                        }`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Smoking */}
            <div className="mb-5 pb-5 border-b border-[#edf1ed]">
              <div className="flex items-center justify-between mb-4">
                <label className="text-[13px] font-medium text-[#24312d]">
                  Do you Smoke?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lifestyleData.smoking}
                    onChange={(e) => handleInputChange("smoking", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {lifestyleData.smoking ? "Yes" : "No"}
                  </span>
                </label>
              </div>

              {lifestyleData.smoking && (
                <div className="mt-4">
                  <label className="block text-[12px] text-[#98a19b] mb-1">
                    How many daily? <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="cigarettesPerDay"
                    value={lifestyleData.cigarettesPerDay}
                    onChange={(e) => handleInputChange("cigarettesPerDay", e.target.value)}
                    placeholder="Number of cigarettes per day"
                    className={`w-full md:w-1/2 h-[38px] border rounded-md px-3 text-sm outline-none ${formErrors.cigarettesPerDay ? "border-red-500" : "border-[#e1e6e1]"
                      }`}
                  />
                </div>
              )}
            </div>

            {/* Drugs */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="text-[13px] font-medium text-[#24312d]">
                  Do you consume drugs?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lifestyleData.drugConsumption}
                    onChange={(e) => handleInputChange("drugConsumption", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {lifestyleData.drugConsumption ? "Yes" : "No"}
                  </span>
                </label>
              </div>

              {lifestyleData.drugConsumption && (
                <div className="mt-4">
                  <label className="block text-[12px] text-[#98a19b] mb-1">
                    How frequently? <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="drugFrequency"
                    value={lifestyleData.drugFrequency}
                    onChange={(e) => handleInputChange("drugFrequency", e.target.value)}
                    className={`w-full md:w-1/2 h-[38px] border rounded-md px-3 text-sm outline-none ${formErrors.drugFrequency ? "border-red-500" : "border-[#e1e6e1]"
                      }`}
                  >
                    <option value="" disabled>Select</option>
                    {drugFrequencyOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Droplets size={16} className="text-green-500" />
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Wellness & Hydration
              </h3>
            </div>

        <div className="mb-4">
  <label className="block text-[13px] text-[#24312d] mb-2">
    Daily water intake (Liters) <span className="text-red-500">*</span>
  </label>
  <input
    type="number"
    name="dailyWaterIntake"
    value={lifestyleData.dailyWaterIntake || ""}
    onChange={(e) => handleInputChange("dailyWaterIntake", e.target.value)}
    placeholder="e.g., 2"
    step="0.1"
    min="0"
    className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.dailyWaterIntake
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
      }`}
  />
  {formErrors.dailyWaterIntake && (
    <p className="text-red-500 text-xs mt-1">{formErrors.dailyWaterIntake}</p>
  )}
</div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] text-[#24312d]">
                  Do you have a sitting job?
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lifestyleData.sittingJob}
                    onChange={(e) => handleInputChange("sittingJob", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#57b26a]"></div>
                  <span className="ml-3 text-sm text-[#24312d]">
                    {lifestyleData.sittingJob ? "Yes" : "No"}
                  </span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Do you travel a lot?
              </label>
              <select
                name="travelFrequency"
                value={lifestyleData.travelFrequency}
                onChange={(e) => handleInputChange("travelFrequency", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              >
                <option value="" disabled>Select</option>
                {travelOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Physical activity / exercise level <span className="text-red-500">*</span>
              </label>
              <select
                name="physicalActivityLevel"
                value={lifestyleData.physicalActivityLevel}
                onChange={(e) => handleInputChange("physicalActivityLevel", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.physicalActivityLevel
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              >
                <option value="" disabled>Select</option>
                {activityOptions.map(opt => (
                  <option key={opt.label} value={opt.label}>{opt.label}</option>
                ))}
              </select>
              {formErrors.physicalActivityLevel && (
                <p className="text-red-500 text-xs mt-1">{formErrors.physicalActivityLevel}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                What type of exercises do you do?
              </label>
              <textarea
                name="exerciseTypes"
                value={lifestyleData.exerciseTypes}
                onChange={(e) => handleInputChange("exerciseTypes", e.target.value)}
                rows="2"
                placeholder="e.g., Walking and yoga, 5 days/week"
                className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
              />
            </div>
          </div>

          {/* Supplements */}
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-[16px] font-semibold text-[#24312d]">
                Supplements
              </h3>
            </div>
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Do you take any supplements?
              </label>
              <textarea
                name="supplements"
                value={lifestyleData.supplements}
                onChange={(e) => handleInputChange("supplements", e.target.value)}
                rows="2"
                placeholder="e.g., Vitamin D, Omega-3"
                className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lifestyle;